import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Pick the section that is actually visible because both mobile and desktop layouts exist in the DOM.
const findVisibleSection = (sectionId: string) => {
  const elements = document.querySelectorAll(`#${sectionId}`) as NodeListOf<HTMLElement>;
  if (!elements.length) {
    return null;
  }
  return Array.from(elements).find((element) => element.getClientRects().length > 0) ?? elements[0];
};

const scrollToSection = (sectionId: string, isMobileView: boolean) => {
  const element = findVisibleSection(sectionId);
  if (!element) {
    return;
  }

  if (isMobileView) {
    const yOffset = -80;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  } else {
    const mainContent = element.closest('main') as HTMLElement | null;
    if (mainContent) {
      if (sectionId === 'projects') {
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const mainRect = mainContent.getBoundingClientRect();
        const targetElement = element.querySelector('h2') || element;
        const targetRect = targetElement.getBoundingClientRect();
        const offsetWithinMain = targetRect.top - mainRect.top;
        // Add some padding offset for better positioning (adjusted for experience section)
        const desiredOffset = sectionId === 'experience' ? -40 : 0;
        const targetTop = Math.max(mainContent.scrollTop + offsetWithinMain + desiredOffset, 0);
        mainContent.scrollTo({ top: targetTop, behavior: 'smooth' });
      }
    }
  }
};

// Shared by the sidebar nav and the command palette so both jump the same way.
export const useSectionNavigation = (setActiveSection: (section: string) => void) => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (sectionId: string, onBeforeScroll?: () => void) => {
      setActiveSection(sectionId);

      const isMobile = window.innerWidth < 1024;
      onBeforeScroll?.();

      if (location.pathname !== '/') {
        navigate('/');
        // Scroll after navigation completes
        setTimeout(() => scrollToSection(sectionId, isMobile), 200);
      } else {
        // Already on home page, scroll on both mobile and desktop
        const scrollDelay = isMobile ? 350 : 0; // Wait for menu close animation on mobile
        setTimeout(() => scrollToSection(sectionId, isMobile), scrollDelay);
      }
    },
    [location.pathname, navigate, setActiveSection]
  );
};
